import React from "react";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Divider,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import AutoStoriesOutlinedIcon from "@mui/icons-material/AutoStoriesOutlined";
import { Artwork } from "../../models/Artwork";

type ArtworkHeaderSectionProps = {
    artwork: Artwork;
    artistLifeSpan: string | null;
};

const ArtworkHeaderSection: React.FC<ArtworkHeaderSectionProps> = ({
    artwork,
    artistLifeSpan,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const artistSubline = [
        artwork.artist?.nationality?.split(",")[0].trim(),
        artistLifeSpan,
    ]
        .filter(Boolean)
        .join(" • ");

    return (
        <Box
            sx={{
                mb: 2,
            }}
        >
            <Box
                sx={{
                    p: { xs: 1.5, sm: 2.5 },
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    bgcolor: "background.paper",
                }}
            >
                <Stack spacing={1.25}>
                    <Box>
                        <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            sx={{ mb: 0.5, flexWrap: "wrap" }}
                        >
                            <PersonOutlineIcon
                                fontSize="small"
                                sx={{ color: "text.secondary" }}
                            />
                            <Typography
                                variant={isMobile ? "h5" : "h4"}
                                sx={{
                                    fontWeight: 700,
                                    lineHeight: 1.15,
                                }}
                            >
                                {artwork.artistName}
                            </Typography>
                        </Stack>

                        {/*{artistSubline && (*/}
                        {/*    <Typography*/}
                        {/*        variant="body2"*/}
                        {/*        color="text.secondary"*/}
                        {/*        sx={{ mb: 0.5 }}*/}
                        {/*    >*/}
                        {/*        {artistSubline}*/}
                        {/*    </Typography>*/}
                        {/*)}*/}

                        {artwork.artist && (
                            <Accordion
                                disableGutters
                                elevation={0}
                                sx={{
                                    mt: 1,
                                    border: "1px solid",
                                    borderColor: "divider",
                                    borderRadius: "12px !important",
                                    overflow: "hidden",
                                    "&:before": { display: "none" },
                                }}
                            >
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    sx={{
                                        bgcolor: "grey.50",
                                        minHeight: 52,
                                        "& .MuiAccordionSummary-content": {
                                            my: 1,
                                        },
                                    }}
                                >
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <AutoStoriesOutlinedIcon
                                            fontSize="small"
                                            sx={{ color: "text.secondary" }}
                                        />
                                        <Typography sx={{ fontWeight: 600 }}>
                                            About the Artist
                                        </Typography>
                                    </Stack>
                                </AccordionSummary>

                                <AccordionDetails sx={{ bgcolor: "background.paper" }}>
                                    <Stack spacing={1}>
                                        <Typography sx={{ fontWeight: 600 }}>
                                            {artwork.artist.artistName}
                                        </Typography>

                                        {artistSubline && (
                                            <Typography variant="body2" color="text.secondary">
                                                {artistSubline}
                                            </Typography>
                                        )}

                                        {artwork.artist.biography && (
                                            <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                                                {artwork.artist.biography}
                                            </Typography>
                                        )}

                                        {artwork.artist.clientBio && (
                                            <Box
                                                sx={{
                                                    mt: 1,
                                                    p: 1.25,
                                                    borderRadius: 1.5,
                                                    bgcolor: "grey.50",
                                                    border: "1px solid",
                                                    borderColor: "divider",
                                                }}
                                            >
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        display: "block",
                                                        fontWeight: 700,
                                                        color: "text.secondary",
                                                        textTransform: "uppercase",
                                                        letterSpacing: 0.3,
                                                        mb: 0.5,
                                                    }}
                                                >
                                                    Client Note
                                                </Typography>

                                                <Typography
                                                    variant="body2"
                                                    sx={{ fontStyle: "italic", color: "text.secondary" }}
                                                >
                                                    {artwork.artist.clientBio}
                                                </Typography>
                                            </Box>
                                        )}
                                    </Stack>
                                </AccordionDetails>
                            </Accordion>
                        )}
                    </Box>

                    <Divider />

                    <Box>
                        <Typography
                            variant={isMobile ? "h6" : "h5"}
                            sx={{
                                fontWeight: 600,
                                lineHeight: 1.2,
                                mb: artwork.artDescription ? 0.75 : 0,
                            }}
                        >
                            {artwork.artWorkTitle || "Untitled"}
                        </Typography>

                        {artwork.artDescription && (
                            <Typography
                                variant="body1"
                                color="text.secondary"
                                sx={{
                                    maxWidth: "100%",
                                    lineHeight: 1.5,
                                }}
                            >
                                {artwork.artDescription}
                            </Typography>
                        )}
                    </Box>

                    
                </Stack>
            </Box>
        </Box>
    );
};

export default ArtworkHeaderSection;